import type { ProductColor } from '@/types'

// Deterministic IDR formatting (identical on server and client): 285000 -> "Rp 285.000"
export const formatIDR = (value: number): string => {
    const rounded = Math.round(Number.isFinite(value) ? value : 0)
    const digits = Math.abs(rounded).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')

    return `${rounded < 0 ? '-' : ''}Rp ${digits}`
}

// English and Indonesian colour words, so a colour typed without a hex code still gets a swatch.
const NAMED_COLORS: Array<[RegExp, string]> = [
    [/blush|pink|rose|sakura|merah muda|pink|mawar/i, '#F3C9CF'],
    [/berry|raspberry|magenta/i, '#C96B7C'],
    [/sage|matcha|green|olive|clover|hijau|daun/i, '#A9BA9D'],
    [/mint|pistachio|tosca/i, '#CFE3D0'],
    [/lilac|lavender|purple|violet|ungu/i, '#D9C6E6'],
    [/sky|blue|biru|langit/i, '#BFD5E6'],
    [/sunshine|yellow|butter|lemon|kuning/i, '#F6D77B'],
    [/orange|maple|ginger|peach|oranye|jingga|persik/i, '#E2A66F'],
    [/rust|terracotta|bata/i, '#B9653F'],
    [/caramel|honey|tan|karamel|madu/i, '#C8956A'],
    [/chocolate|cocoa|brown|cokelat|coklat/i, '#7A5640'],
    [/biscuit|beige|sand|oat|latte|pasir/i, '#DCC5A8'],
    [/grey|gray|silver|dove|abu/i, '#BDB5AE'],
    [/black|charcoal|hitam/i, '#3B3532'],
    [/red|cherry|merah/i, '#C9474F'],
    [/white|snow|cream|vanilla|ivory|moon|putih|krem/i, '#F4EBDD']
]

export const normalizeHex = (value: string | null | undefined): string | null => {
    if (!value) return null

    const match = value.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)

    if (!match) return null

    const hex = match[1].length === 3
        ? match[1].split('').map(char => char + char).join('')
        : match[1]

    return `#${hex.toUpperCase()}`
}

// Product colours are stored as "Label #HEX" strings, the hex part is optional.
export const parseColor = (value: string): ProductColor => {
    const match = value.match(/#([0-9a-f]{6}|[0-9a-f]{3})\b/i)
    const hex = match ? normalizeHex(match[0]) : null
    const label = (match ? value.replace(match[0], '') : value).replace(/\s+/g, ' ').trim()
    const named = NAMED_COLORS.find(([pattern]) => pattern.test(label))

    return {
        label: label || hex || 'Warna',
        hex: hex ?? (named ? named[1] : null)
    }
}

// The stored form of a colour option: "Label #HEX".
export const serializeColor = (color: { label: string, hex: string | null }): string => {
    const label = color.label.replace(/#[0-9a-f]{3,6}\b/gi, '').replace(/\s+/g, ' ').trim()
    const hex = normalizeHex(color.hex)

    return [label, hex].filter(Boolean).join(' ')
}

// Deterministic date formatting for Indonesian readers: "3 Nov 2026, 14.05" (UTC+7, WIB).
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']

export const formatDateTime = (iso: string | null | undefined, withTime = true): string => {
    if (!iso) return '-'

    const time = Date.parse(iso)

    if (Number.isNaN(time)) return '-'

    const local = new Date(time + 7 * 60 * 60 * 1000)
    const date = `${local.getUTCDate()} ${MONTHS[local.getUTCMonth()]} ${local.getUTCFullYear()}`

    if (!withTime) return date

    const hours = String(local.getUTCHours()).padStart(2, '0')
    const minutes = String(local.getUTCMinutes()).padStart(2, '0')

    return `${date}, ${hours}.${minutes} WIB`
}

export const slugify = (value: string): string => {
    return value
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/&/g, ' dan ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80)
        .replace(/-+$/g, '')
}

export const parseJsonArray = (value: unknown): Array<string> => {
    if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')
    if (typeof value !== 'string' || !value.trim()) return []

    try {
        const parsed = JSON.parse(value)

        return Array.isArray(parsed)
            ? parsed.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
            : []
    }
    catch {
        return value.split(',').map(item => item.trim()).filter(Boolean)
    }
}

export const stockLabel = (stock: number, madeToOrder: boolean): { label: string, tone: 'ok' | 'low' | 'out' | 'custom' } => {
    if (madeToOrder) return { label: 'Dibuat sesuai pesanan', tone: 'custom' }
    if (stock <= 0) return { label: 'Stok Habis', tone: 'out' }
    if (stock <= 3) return { label: `Sisa ${stock}`, tone: 'low' }

    return { label: `Tersedia, stok ${stock}`, tone: 'ok' }
}
